import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate, useOutletContext } from 'react-router-dom';
import { useAxios, getCurrentLanguage } from '../../core/_axios';
import { useAuth } from '../../AuthContext';
import { EmailCampaign, CampaignFilters } from '../../core/_models_email';
import {
  getPendingEmailCampaigns,
  approveEmailCampaign,
  rejectEmailCampaign,
  batchApproveEmailCampaigns
} from '../../core/_requests_email';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmailReviewModal from '../../components/EmailReviewModal';
import useEmailCampaignWebSocket from '../../hooks/useEmailCampaignWebSocket';

// Configurable email generation time estimate (in seconds)
const EMAIL_GENERATION_TIME = 30;

// Beautiful quotes about grief, loss, and remembrance
// Supporting both English and Spanish
// Format for easy ChatGPT extension: { text: "quote", author: "attribution" }
export const getMemorialQuotes = (language: string = 'en') => {
  const quotes: Record<string, Array<{ text: string; author: string }>> = {
    en: [
      {
        text: "What we have once enjoyed we can never lose. All that we love deeply becomes a part of us.",
        author: "Helen Keller",
      },
      {
        text: "Give sorrow words; the grief that does not speak whispers the o’er-fraught heart and bids it break.",
        author: "William Shakespeare (Macbeth, Act 4, Scene 3)",
      },
      {
        text: "Grief is the price we pay for love.",
        author: "Queen Elizabeth II",
      },
      {
        text: "For life and death are one, even as the river and the sea are one.",
        author: "Kahlil Gibran",
      },
      {
        text: "Unable are the loved to die, for Love is Immortality.",
        author: "Emily Dickinson",
      },
      {
        text: "No one ever told me that grief felt so like fear.",
        author: "C. S. Lewis",
      },
      {
        text: "To live in hearts we leave behind is not to die.",
        author: "Thomas Campbell",
      },
      {
        text: "The life of the dead is placed in the memory of the living.",
        author: "Marcus Tullius Cicero",
      },
      {
        text: "There are stars whose radiance is visible on earth though they have long disappeared. There are people whose brilliance continues to light the world even though they are no longer among the living.",
        author: "Hannah Senesh",
      },
      {
        text: "Tears are the silent language of grief.",
        author: "Voltaire",
      },
      {
        text: "The song is ended, but the melody lingers on.",
        author: "Irving Berlin",
      },
      {
        text: "What is lovely never dies, but passes into other loveliness.",
        author: "Thomas Bailey Aldrich",
      },
      // NEW QUOTES BELOW
      {
        text: "Those we love and lose are always connected by heartstrings into infinity.",
        author: "Terri Guillemets",
      },
      {
        text: "Death leaves a heartache no one can heal, love leaves a memory no one can steal.",
        author: "From an Irish headstone",
      },
      {
        text: "How lucky I am to have something that makes saying goodbye so hard.",
        author: "A. A. Milne (Winnie-the-Pooh)",
      },
      {
        text: "When someone you love becomes a memory, the memory becomes a treasure.",
        author: "Anonymous",
      },
      {
        text: "They that love beyond the world cannot be separated by it. Death cannot kill what never dies.",
        author: "William Penn",
      },
      {
        text: "Perhaps they are not stars, but rather openings in heaven where the love of our lost ones pours through and shines down upon us.",
        author: "Eskimo Proverb",
      },
      {
        text: "The reality is that you will grieve forever. You will not 'get over' the loss... you will learn to live with it.",
        author: "Elisabeth Kübler-Ross",
      },
      {
        text: "Love is stronger than death even though it can't stop death from happening.",
        author: "Anonymous",
      },
      {
        text: "Loss is nothing else but change, and change is Nature's delight.",
        author: "Marcus Aurelius",
      },
      {
        text: "The pain passes, but the beauty remains.",
        author: "Pierre-Auguste Renoir",
      },
      {
        text: "We understand death only after it has placed its hands on someone we love.",
        author: "Anne L. de Staël",
      },
      {
        text: "To weep is to make less the depth of grief.",
        author: "William Shakespeare (Henry VI, Part II, Act 2, Scene 1)",
      },
    ],
    es: [
      {
        text: "Lo que una vez hemos disfrutado no podemos perderlo. Todo lo que amamos profundamente se vuelve parte de nosotros.",
        author: "Helen Keller",
      },
      {
        text: "Pon al dolor en palabras; el duelo que no habla susurra al corazón sobrecargado y lo hace quebrarse.",
        author: "William Shakespeare (Macbeth, Acto 4, Escena 3)",
      },
      {
        text: "El duelo es el precio que pagamos por el amor.",
        author: "Isabel II del Reino Unido",
      },
      {
        text: "Porque la vida y la muerte son una, así como el río y el mar son uno.",
        author: "Kahlil Gibran",
      },
      {
        text: "Incapaces son los amados de morir, pues el Amor es Inmortalidad.",
        author: "Emily Dickinson",
      },
      {
        text: "Nadie me dijo jamás que el duelo se sintiera tan parecido al miedo.",
        author: "C. S. Lewis",
      },
      {
        text: "Vivir en los corazones que dejamos atrás no es morir.",
        author: "Thomas Campbell",
      },
      {
        text: "La vida de los muertos está puesta en la memoria de los vivos.",
        author: "Marco Tulio Cicerón",
      },
      {
        text: "Hay estrellas cuya luz aún es visible en la tierra aunque hace tiempo desaparecieron. Hay personas cuya brillantez sigue iluminando el mundo aunque ya no estén entre los vivos.",
        author: "Hannah Senesh",
      },
      {
        text: "Las lágrimas son el lenguaje silencioso del duelo.",
        author: "Voltaire",
      },
      {
        text: "La canción termina, pero la melodía perdura.",
        author: "Irving Berlin",
      },
      {
        text: "Lo que es bello nunca muere, sino que pasa a otra hermosura.",
        author: "Thomas Bailey Aldrich",
      },
      // NEW QUOTES BELOW
      {
        text: "Aquellos que amamos y perdemos siempre están conectados por cuerdas del corazón hasta el infinito.",
        author: "Terri Guillemets",
      },
      {
        text: "La muerte deja un dolor que nadie puede sanar, el amor deja un recuerdo que nadie puede robar.",
        author: "De una lápida irlandesa",
      },
      {
        text: "Qué afortunado soy de tener algo que hace tan difícil decir adiós.",
        author: "A. A. Milne (Winnie-the-Pooh)",
      },
      {
        text: "Cuando alguien que amas se convierte en un recuerdo, el recuerdo se convierte en un tesoro.",
        author: "Anónimo",
      },
      {
        text: "Aquellos que aman más allá de este mundo no pueden ser separados por él. La muerte no puede matar lo que nunca muere.",
        author: "William Penn",
      },
      {
        text: "Tal vez no sean estrellas, sino aberturas en el cielo por donde se vierte el amor de nuestros seres perdidos y brilla sobre nosotros.",
        author: "Proverbio esquimal",
      },
      {
        text: "La realidad es que lamentarás para siempre. No 'superarás' la pérdida... aprenderás a vivir con ella.",
        author: "Elisabeth Kübler-Ross",
      },
      {
        text: "El amor es más fuerte que la muerte, aunque no pueda impedir que la muerte ocurra.",
        author: "Anónimo",
      },
      {
        text: "La pérdida no es otra cosa que cambio, y el cambio es el deleite de la Naturaleza.",
        author: "Marco Aurelio",
      },
      {
        text: "El dolor pasa, pero la belleza permanece.",
        author: "Pierre-Auguste Renoir",
      },
      {
        text: "Solo comprendemos la muerte después de que pone sus manos sobre alguien a quien amamos.",
        author: "Anne L. de Staël",
      },
      {
        text: "Llorar es disminuir la profundidad del dolor.",
        author: "William Shakespeare (Enrique VI, Parte II, Acto 2, Escena 1)",
      },
    ],
  };

  return quotes[language] || quotes.en;
};

interface TrainingProgress {
  completed_count: number;
  required_count: number;
  is_complete: boolean;
  remaining: number;
}

const EmailReviewQueue: React.FC = () => {
  const axios = useAxios();
  const { currentUserAccountUniqueId, viewingAllAccounts, currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { trainingProgress, refreshTrainingProgress } = useOutletContext<{
    trainingProgress: TrainingProgress | null;
    refreshTrainingProgress: () => void;
  }>();

  const [loading, setLoading] = useState(true);
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [selectedCampaigns, setSelectedCampaigns] = useState<Set<string>>(new Set());
  const [filters, setFilters] = useState<CampaignFilters>({
    min_score: 0
  });
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<EmailCampaign | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingCampaignId, setPendingCampaignId] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [navigatedFromManualDraft, setNavigatedFromManualDraft] = useState(false);
  const [hasProcessedNavigation, setHasProcessedNavigation] = useState(false);
  const [countdown, setCountdown] = useState(EMAIL_GENERATION_TIME);
  const [currentFactIndex, setCurrentFactIndex] = useState(0);

  // Use ref to track selected campaign for WebSocket callbacks
  const selectedCampaignRef = useRef<EmailCampaign | null>(null);
  useEffect(() => {
    selectedCampaignRef.current = selectedCampaign;
  }, [selectedCampaign]);


  // Define sortCampaigns first since it's used by other callbacks
  const sortCampaigns = useCallback((campaigns: EmailCampaign[]) => {
    return [...campaigns].sort((a, b) => {
      // Pre-need interested campaigns first
      const aPreNeed = a.lead_score_breakdown?.interested_in_pre_need || 0;
      const bPreNeed = b.lead_score_breakdown?.interested_in_pre_need || 0;
      if (aPreNeed > 0 && bPreNeed === 0) return -1;
      if (bPreNeed > 0 && aPreNeed === 0) return 1;

      // Then by lead score
      return b.lead_score - a.lead_score;
    });
  }, []);

  // Define data loading functions
  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const pendingCampaigns = await getPendingEmailCampaigns(axios, {
        limit: 50,
        offset: 0,
        all_accounts: viewingAllAccounts
      });

      // Filter by score on the client side if needed
      const filtered = pendingCampaigns.filter(campaign =>
        campaign.lead_score >= (filters.min_score || 0)
      );

      // Sort campaigns using shared function
      const sorted = sortCampaigns(filtered);

      setCampaigns(sorted);
    } catch (err) {
      console.error('Failed to load campaigns:', err);
      setError('Failed to load email campaigns. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [axios, filters.min_score, sortCampaigns, viewingAllAccounts]);

  // Modal functions - removed useCallback to prevent dependency issues
  const openReviewModal = (campaign: EmailCampaign) => {
    console.log('[EmailReviewQueue] openReviewModal called for campaign:', campaign.id);
    setSelectedCampaign(campaign);
    setIsModalOpen(true);
    // Clear waiting state when modal opens
    setWaitingForCampaignId(null);
    setPendingCampaignId(null);
  };

  const closeReviewModal = () => {
    setSelectedCampaign(null);
    setIsModalOpen(false);
    // Clear ALL waiting states when closing modal
    setWaitingForCampaignId(null);
    setPendingCampaignId(null);
  };

  // WebSocket callbacks - removed useCallback to prevent infinite loops
  const handleCampaignCreated = (campaign: EmailCampaign) => {
    console.log('[EmailReviewQueue] Campaign created:', campaign.id, 'Waiting for:', waitingForCampaignIdRef.current);

    // Add if it's a pending_review or generating campaign
    if (campaign.status === 'pending_review' || campaign.status === 'generating') {
      setCampaigns(prev => {
        // Check if campaign already exists
        const exists = prev.some(c => c.id === campaign.id);
        if (exists) return prev;

        // Add new campaign and re-sort
        const updated = [campaign, ...prev];
        return sortCampaigns(updated);
      });

      // Show notification only for ready campaigns
      if (campaign.status === 'pending_review') {
        setSuccessMessage('New email campaign received for review');
        setTimeout(() => setSuccessMessage(null), 3000);
      }

      // If we're waiting for this specific campaign, open the modal even if it's still generating
      // Convert both to strings for comparison to handle type mismatches
      if (waitingForCampaignIdRef.current && String(waitingForCampaignIdRef.current) === String(campaign.id)) {
        console.log('[EmailReviewQueue] Opening review modal for campaign:', campaign.id, 'Status:', campaign.status);
        openReviewModal(campaign);
        // Clear the pending campaign ID to prevent re-showing the modal
        setPendingCampaignId(null);
        setNavigatedFromManualDraft(false); // Clear flag after opening modal
      }
    }
  };

  const handleCampaignUpdated = (campaign: EmailCampaign) => {
    console.log('[EmailReviewQueue] Campaign updated:', campaign.id, 'Status:', campaign.status, 'Waiting for:', waitingForCampaignIdRef.current);

    setCampaigns(prev => {
      // If status is not pending_review or generating, remove from list
      if (campaign.status !== 'pending_review' && campaign.status !== 'generating') {
        return prev.filter(c => c.id !== campaign.id);
      }

      // Otherwise, update the campaign
      return prev.map(c => c.id === campaign.id ? campaign : c);
    });

    // Check if this is the campaign we're waiting for and it's now ready
    if (waitingForCampaignIdRef.current && String(waitingForCampaignIdRef.current) === String(campaign.id) && campaign.status === 'pending_review') {
      console.log('[EmailReviewQueue] Campaign is ready, opening review modal for:', campaign.id);
      openReviewModal(campaign);
      // Clear the pending campaign ID to prevent re-showing the modal
      setPendingCampaignId(null);
      setNavigatedFromManualDraft(false); // Clear flag after opening modal
    }

    // Update the modal if it's open with this campaign (important for generating -> ready transition)
    if (selectedCampaignRef.current?.id === campaign.id) {
      setSelectedCampaign(campaign);
    }
  };

  // Use WebSocket channel for real-time updates
  const { isConnected } = useEmailCampaignWebSocket({
    onCampaignCreated: handleCampaignCreated,
    onCampaignUpdated: handleCampaignUpdated
  });

  // Load data when component mounts or filters change
  useEffect(() => {
    // Load campaigns when we have a specific account OR when viewing all accounts
    if (currentUserAccountUniqueId || viewingAllAccounts) {
      loadCampaigns();
    } else {
      setLoading(false);
    }
  }, [filters.min_score, currentUserAccountUniqueId, viewingAllAccounts, loadCampaigns]);

  // Handle navigation state (when coming from manual draft)
  useEffect(() => {
    if (location.state?.openCampaignId && !hasProcessedNavigation) {
      setHasProcessedNavigation(true); // Mark as processed
      console.log('[EmailReviewQueue] Received openCampaignId from navigation:', location.state.openCampaignId);
      setPendingCampaignId(location.state.openCampaignId);
      setNavigatedFromManualDraft(true); // Set flag when navigating from manual draft

      // Check if the campaign already exists in our list
      const existingCampaign = campaigns.find(c => String(c.id) === String(location.state.openCampaignId));
      if (existingCampaign) {
        console.log('[EmailReviewQueue] Campaign already exists, opening modal immediately');
        openReviewModal(existingCampaign);
        // Clear pending state since we found the campaign
        setPendingCampaignId(null);
        setNavigatedFromManualDraft(false); // Clear flag after opening modal
      }

      // Clear the location state immediately
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openCampaignId, hasProcessedNavigation, campaigns, navigate, location.pathname]);

  // Track if we're waiting to open a specific campaign
  const [waitingForCampaignId, setWaitingForCampaignId] = useState<string | null>(null);
  const waitingForCampaignIdRef = useRef<string | null>(null);
  useEffect(() => {
    waitingForCampaignIdRef.current = waitingForCampaignId;
  }, [waitingForCampaignId]);

  // Countdown effect when waiting for campaign
  useEffect(() => {
    if (navigatedFromManualDraft && waitingForCampaignId) {
      setCountdown(EMAIL_GENERATION_TIME); // Reset countdown
      // Pick a random quote once when starting
      const quotes = getMemorialQuotes(getCurrentLanguage());
      setCurrentFactIndex(Math.floor(Math.random() * quotes.length));

      const countdownInterval = setInterval(() => {
        setCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => {
        clearInterval(countdownInterval);
      };
    }
  }, [navigatedFromManualDraft, waitingForCampaignId]);

  // Set waiting state when we have a pending campaign ID
  useEffect(() => {
    // When viewing all accounts, we don't have WebSocket so always set waiting state
    // When viewing specific account, only set if WebSocket is connected
    const shouldSetWaiting = viewingAllAccounts
      ? (pendingCampaignId && !loading && !isProcessing)
      : (pendingCampaignId && !loading && isConnected && !isProcessing);

    if (shouldSetWaiting) {
      console.log('[EmailReviewQueue] Setting waiting for campaign ID:', pendingCampaignId, 'WebSocket connected:', isConnected, 'Viewing all accounts:', viewingAllAccounts);
      setWaitingForCampaignId(pendingCampaignId);
    }
  }, [pendingCampaignId, loading, isConnected, isProcessing, viewingAllAccounts]);

  // Poll for campaign when viewing all accounts and waiting for a specific campaign
  useEffect(() => {
    if (!viewingAllAccounts || !waitingForCampaignId || !navigatedFromManualDraft) {
      return;
    }

    console.log('[EmailReviewQueue] Starting polling for campaign:', waitingForCampaignId);
    let pollCount = 0;
    const maxPolls = 30; // Poll for up to 30 seconds

    const pollInterval = setInterval(async () => {
      pollCount++;
      console.log(`[EmailReviewQueue] Polling attempt ${pollCount}/${maxPolls}`);

      try {
        // Fetch fresh campaigns data
        const pendingCampaigns = await getPendingEmailCampaigns(axios, {
          limit: 50,
          offset: 0,
          all_accounts: viewingAllAccounts
        });

        // Check if the campaign now exists
        const foundCampaign = pendingCampaigns.find(c => String(c.id) === String(waitingForCampaignId));
        if (foundCampaign) {
          console.log('[EmailReviewQueue] Found campaign via polling:', foundCampaign.id);

          // Update campaigns state with the new data
          const filtered = pendingCampaigns.filter(campaign =>
            campaign.lead_score >= (filters.min_score || 0)
          );
          const sorted = sortCampaigns(filtered);
          setCampaigns(sorted);

          // Open the modal with the found campaign
          openReviewModal(foundCampaign);
          clearInterval(pollInterval);
        } else if (pollCount >= maxPolls) {
          console.log('[EmailReviewQueue] Polling timeout - campaign not found');
          setWaitingForCampaignId(null);
          setPendingCampaignId(null);
          setNavigatedFromManualDraft(false);
          setError('Campaign creation is taking longer than expected. Please refresh to see new campaigns.');
          clearInterval(pollInterval);
        }
      } catch (err) {
        console.error('[EmailReviewQueue] Polling error:', err);
        clearInterval(pollInterval);
      }
    }, 1000); // Poll every second

    return () => {
      clearInterval(pollInterval);
    };
  }, [viewingAllAccounts, waitingForCampaignId, navigatedFromManualDraft, axios, filters.min_score, sortCampaigns]);

  const handleApprove = async (campaignId: string, updatedContent?: string | { email_content: string; email_subject: string }) => {
    console.log('[handleApprove] Starting approval process');
    // Set processing flag to prevent modal from showing
    setIsProcessing(true);

    // Clear ALL waiting states FIRST to prevent modal from reappearing
    setWaitingForCampaignId(null);
    setPendingCampaignId(null);
    setNavigatedFromManualDraft(false); // Ensure this is cleared
    setHasProcessedNavigation(true); // Ensure navigation won't be reprocessed

    // Close the modal immediately
    closeReviewModal();

    // Then remove from list for instant feedback
    setCampaigns(prev => prev.filter(c => c.id !== campaignId));
    setSelectedCampaigns(prev => {
      const newSet = new Set(prev);
      newSet.delete(campaignId);
      return newSet;
    });

    try {
      await approveEmailCampaign(axios, campaignId, updatedContent);

      setSuccessMessage('Email approved and queued for sending!');

      // Refresh training progress immediately after approval
      if (refreshTrainingProgress) {
        console.log('[EmailReviewQueue] Refreshing training progress after approval');
        refreshTrainingProgress();
      }

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to approve campaign:', err);
      setError('Failed to approve campaign. Please try again.');

      // Re-add the campaign if approval failed
      loadCampaigns();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReject = async (campaignId: string, reason: string) => {
    console.log('[handleReject] Starting rejection process');
    // Set processing flag to prevent modal from showing
    setIsProcessing(true);

    // Clear ALL waiting states FIRST to prevent modal from reappearing
    setWaitingForCampaignId(null);
    setPendingCampaignId(null);
    setNavigatedFromManualDraft(false); // Ensure this is cleared

    // Close the modal immediately
    closeReviewModal();

    // Then remove from list for instant feedback
    setCampaigns(prev => prev.filter(c => c.id !== campaignId));
    setSelectedCampaigns(prev => {
      const newSet = new Set(prev);
      newSet.delete(campaignId);
      return newSet;
    });

    try {
      await rejectEmailCampaign(axios, campaignId, reason);

      setSuccessMessage('Email rejected');

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to reject campaign:', err);
      setError('Failed to reject campaign. Please try again.');

      // Re-add the campaign if rejection failed
      loadCampaigns();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBatchApprove = async () => {
    if (selectedCampaigns.size === 0) return;

    const confirm = window.confirm(`Approve ${selectedCampaigns.size} selected campaigns?`);
    if (!confirm) return;

    try {
      const result = await batchApproveEmailCampaigns(axios, Array.from(selectedCampaigns));

      setSuccessMessage(`Successfully approved ${result.succeeded} campaigns`);
      setCampaigns(campaigns.filter(c => !selectedCampaigns.has(c.id)));
      setSelectedCampaigns(new Set());

      if (result.failed > 0) {
        console.error('Some campaigns failed:', result.errors);
      }

      // Refresh training progress immediately after batch approval
      if (refreshTrainingProgress) {
        console.log('[EmailReviewQueue] Refreshing training progress after batch approval');
        refreshTrainingProgress();
      }

      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Failed to batch approve:', err);
      setError('Failed to approve campaigns. Please try again.');
    }
  };

  const toggleSelectAll = () => {
    if (selectedCampaigns.size === campaigns.length) {
      setSelectedCampaigns(new Set());
    } else {
      setSelectedCampaigns(new Set(campaigns.map(c => c.id)));
    }
  };

  const getLeadScoreBadge = (score: number) => {
    if (score >= 80) return 'badge-success';
    if (score >= 60) return 'badge-warning';
    if (score >= 40) return 'badge-info';
    return 'badge-secondary';
  };

  // Date formatting function to match ManualEmailDraft
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  // Log modal display conditions - must be before any conditional returns
  useEffect(() => {
    console.log('[Modal Display Debug]', {
      navigatedFromManualDraft,
      waitingForCampaignId,
      pendingCampaignId,
      isProcessing,
      shouldShowModal: navigatedFromManualDraft && waitingForCampaignId
    });
  }, [navigatedFromManualDraft, waitingForCampaignId, pendingCampaignId, isProcessing]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      {/* Loading overlay when waiting for a campaign - ONLY show if we navigated from manual draft */}
      {navigatedFromManualDraft && waitingForCampaignId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 shadow-xl max-w-lg">
            <div className="text-center">
              {/* Memorial quote display at the top */}
              <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg p-5 mb-6 min-h-[100px] border border-gray-100">
                <div className="flex flex-col space-y-3">
                  <svg className="w-6 h-6 text-gray-400 opacity-50 mx-auto" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                  <p className="text-sm text-gray-700 leading-relaxed font-medium italic">
                    {getMemorialQuotes(getCurrentLanguage())[currentFactIndex]?.text}
                  </p>
                  <p className="text-xs text-gray-500">
                    — {getMemorialQuotes(getCurrentLanguage())[currentFactIndex]?.author}
                  </p>
                </div>
              </div>

              <h3 className="text-xl font-semibold text-gray-900 mb-2 flex items-center justify-center gap-3">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-300 border-t-blue-600"></div>
                <span>{getCurrentLanguage() === 'es' ? 'Creando mensaje...' : 'Creating message...'}</span>
              </h3>

              <p className="text-gray-600 mb-4">
                {getCurrentLanguage() === 'es'
                  ? 'Estamos escribiendo un correo personalizado.'
                  : 'We\'re writing a personalized email.'}
              </p>

              {/* Simplified countdown */}
              <p className="text-sm text-gray-500">
                {countdown > 0 ? (
                  getCurrentLanguage() === 'es'
                    ? `Finalización estimada en ${countdown} segundo${countdown !== 1 ? 's' : ''}`
                    : `Estimated completion in ${countdown} second${countdown !== 1 ? 's' : ''}`
                ) : (
                  getCurrentLanguage() === 'es' ? 'Finalizando...' : 'Finalizing...'
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Alerts positioned at top of page */}
      {error && (
        <div className="container-fixed mb-4">
          <div className="alert alert-danger flex justify-between items-center">
            <span>{error}</span>
            <button
              className="btn btn-sm btn-icon btn-light"
              onClick={() => setError(null)}
            >
              <i className="ki-filled ki-cross"></i>
            </button>
          </div>
        </div>
      )}

      {successMessage && (
        <div className="container-fixed mb-4">
          <div className="alert alert-success flex justify-between items-center">
            <span>{successMessage}</span>
            <button
              className="btn btn-sm btn-icon btn-light"
              onClick={() => setSuccessMessage(null)}
            >
              <i className="ki-filled ki-cross"></i>
            </button>
          </div>
        </div>
      )}

      <div className="card card-grid min-w-full">
        <div className="card-header flex-wrap py-3">
          <h3 className="card-title">
            Review Queue ({campaigns.length})
            {isConnected && (
              <span className="badge badge-light-success badge-sm ms-3" title="Live updates enabled">
                <i className="ki-filled ki-wifi text-success"></i> Live
              </span>
            )}
          </h3>

          <div className="flex gap-4">
            {/* Score Filter - Only show when there are campaigns */}
            {campaigns.length > 0 && (
              <select
                className="form-select form-select-sm form-select-solid"
                value={filters.min_score || 0}
                onChange={(e) => setFilters({ ...filters, min_score: parseInt(e.target.value) })}
              >
                <option value="0">All Scores</option>
                <option value="40">40+ Score</option>
                <option value="60">60+ Score</option>
                <option value="80">80+ Score</option>
              </select>
            )}

            {selectedCampaigns.size > 0 && (
              <button
                className="btn btn-sm btn-success"
                onClick={handleBatchApprove}
              >
                Approve {selectedCampaigns.size}
              </button>
            )}
          </div>
        </div>

        <div className="card-body p-0">

          {campaigns.length === 0 ? (
            <div className="text-center py-10">
              <i className="ki-filled ki-mail text-5xl text-gray-400 mb-5"></i>
              <h3 className="text-gray-700 text-lg font-medium mb-2">No messages waiting for review</h3>
              <p className="text-gray-500 mb-6 max-w-md mx-auto">
                Thank-you messages are automatically suggested when guests with email addresses sign the guestbook.
                You'll review and approve them here before they're sent.
              </p>
              <Link
                to="/follow-up-messages/manual-draft"
                className="btn btn-primary"
              >
                <i className="ki-filled ki-plus-circle mr-2"></i>
                Create Manual Message
              </Link>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-border">
                <thead>
                  <tr className="text-gray-600 fw-medium">
                    <th className="w-10">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={selectedCampaigns.size === campaigns.length}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    {(viewingAllAccounts || !currentUserAccountUniqueId) && (
                      <th className="min-w-[150px]">Account</th>
                    )}
                    <th className="min-w-[200px]">Signer</th>
                    <th className="min-w-[150px]">Service</th>
                    <th className="min-w-[300px]">Email</th>
                    <th className="w-[100px]">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {campaigns.map((campaign) => (
                    <tr
                      key={campaign.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={(e) => {
                        // Don't open modal if clicking checkbox
                        if ((e.target as HTMLInputElement).type !== 'checkbox') {
                          openReviewModal(campaign);
                        }
                      }}
                    >
                      <td onClick={(e) => e.stopPropagation()}>
                        <input
                          className="form-check-input"
                          type="checkbox"
                          checked={selectedCampaigns.has(campaign.id)}
                          onChange={(e) => {
                            const newSet = new Set(selectedCampaigns);
                            if (e.target.checked) {
                              newSet.add(campaign.id);
                            } else {
                              newSet.delete(campaign.id);
                            }
                            setSelectedCampaigns(newSet);
                          }}
                        />
                      </td>
                      {(viewingAllAccounts || !currentUserAccountUniqueId) && (
                        <td>
                          <div className="text-gray-800 fw-medium text-sm">
                            {campaign.account_name || 'Unknown Account'}
                          </div>
                        </td>
                      )}
                      <td>
                        <div className="d-flex flex-column">
                          <div className="d-flex align-items-center mb-1">
                            <span className="text-gray-800 fw-medium">
                              {campaign.signer_info?.name || 'Unknown'}
                            </span>
                            <span className={`badge ${getLeadScoreBadge(campaign.lead_score)} badge-sm ms-3`}>
                              {campaign.lead_score}
                            </span>
                          </div>
                          <div className="text-gray-600 text-xs">
                            {campaign.signer_info?.email || 'No email'}
                            {campaign.signer_info?.phone && (
                              <span className="ms-2">• {campaign.signer_info.phone}</span>
                            )}
                          </div>
                          {(campaign.lead_score_breakdown?.interested_in_pre_need > 0 || campaign.is_dry_run) && (
                            <div className="d-flex gap-1 mt-1">
                              {campaign.lead_score_breakdown?.interested_in_pre_need > 0 && (
                                <span className="badge badge-light-primary badge-sm">Pre-Need</span>
                              )}
                              {campaign.is_dry_run && (
                                <span className="badge badge-light-warning badge-sm">Dry Run</span>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="text-gray-800 fw-medium">
                          {campaign.service_unique_identifier ? (
                            <Link
                              to={`/services/${campaign.service_unique_identifier}`}
                              className="text-primary hover:text-primary-dark"
                            >
                              {campaign.deceased_info?.name || campaign.service_name || 'Unknown'}
                            </Link>
                          ) : (
                            campaign.deceased_info?.name || campaign.service_name || 'Unknown'
                          )}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex flex-column">
                          <div className="text-gray-800 fw-medium text-sm mb-1">
                            {campaign.email_subject}
                          </div>
                          <div className="text-gray-600 text-xs">
                            {campaign.final_email_content
                              ? campaign.final_email_content.length > 80
                                ? `${campaign.final_email_content.substring(0, 80)}...`
                                : campaign.final_email_content
                              : 'No content'
                            }
                          </div>
                        </div>
                      </td>
                      <td className="text-gray-600 text-xs">
                        {formatDate(campaign.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <EmailReviewModal
        campaign={selectedCampaign}
        isOpen={isModalOpen}
        onClose={closeReviewModal}
        onApprove={handleApprove}
        onReject={handleReject}
        currentUser={currentUser}
        trainingProgress={trainingProgress}
      />
    </>
  );
};

export default EmailReviewQueue;